import moment from 'moment'

/**
 * Prazo de exibição dos itens já terminados no Kanban. Sem isso a faixa de estado
 * final só cresce: nada nunca sai de lá.
 */

export type PeriodUnit = 'day' | 'week' | 'month' | 'year'
export type CurrentUnit = 'week' | 'month' | 'year'

export type FinishedPeriod =
    | { kind: 'all' }
    | { kind: 'last', amount: number, unit: PeriodUnit }    //janela móvel: últimos N dias/semanas/...
    | { kind: 'current', unit: CurrentUnit }                //semana/mês/ano corrente
    | { kind: 'since', date: string }                       //'YYYY-MM-DD'

export const DEFAULT_PERIOD: FinishedPeriod = {kind: 'last', amount: 1, unit: 'week'}

/**
 * O prazo de cada raia, por id do valor.
 *
 * Mora aqui e não no storage: o tipo é sobre PRAZO, não sobre armazenamento, e é o storage
 * que importa deste módulo — declarado lá, o cálculo do padrão abaixo fecharia um ciclo. O
 * storage o reexporta para quem já o importava de lá.
 *
 * A chave de um objeto JSON é sempre texto, então o que volta da leitura é `"3"` e não `3` —
 * daí o índice por string, com o id coagido na entrada e na saída.
 */
export type KanbanPeriods = { [valueId: string]: FinishedPeriod }

/**
 * O prazo de cada raia terminal: o guardado, ou o padrão para quem nunca escolheu.
 *
 * O tabuleiro e o sandbox montavam este mapa com o mesmo laço, cada um no seu arquivo. O que
 * legitimamente difere entre eles é de onde vem o `stored`, e isso continua com cada um.
 */
export function periodsFor(finalIds: number[], stored: KanbanPeriods): KanbanPeriods {
    const periods: KanbanPeriods = {}
    finalIds.forEach(id => periods[id] = stored[id] || DEFAULT_PERIOD)
    return periods
}

const UNITS: PeriodUnit[] = ['day', 'week', 'month', 'year']
const CURRENT_UNITS: CurrentUnit[] = ['week', 'month', 'year']

/** Ordem em que os presets aparecem no menu. O divisor fica a cargo da view. */
export const PRESETS: FinishedPeriod[] = [
    {kind: 'all'},
    {kind: 'last', amount: 1, unit: 'day'},
    {kind: 'last', amount: 1, unit: 'week'},
    {kind: 'last', amount: 1, unit: 'month'},
    {kind: 'last', amount: 1, unit: 'year'},
    {kind: 'current', unit: 'week'},
    {kind: 'current', unit: 'month'},
    {kind: 'current', unit: 'year'}
]

/** Identidade estrutural, para marcar o preset selecionado e comparar períodos. */
export function periodKey(period: FinishedPeriod): string {
    switch (period.kind) {
        case 'all':
            return 'all'
        case 'last':
            return `last:${period.amount}:${period.unit}`
        case 'current':
            return `current:${period.unit}`
        case 'since':
            return `since:${period.date}`
    }
}

export function isPreset(period: FinishedPeriod): boolean {
    const key = periodKey(period)
    return PRESETS.some(preset => periodKey(preset) === key)
}

/** Instante a partir do qual um item terminado ainda aparece. null = sem limite. */
export function periodStart(period: FinishedPeriod, now: Date = new Date()): Date | null {
    switch (period.kind) {
        case 'all':
            return null
        case 'last':
            return moment(now).subtract(period.amount, period.unit).toDate()
        case 'current':
            return moment(now).startOf(period.unit).toDate()
        case 'since': {
            const start = moment(period.date, 'YYYY-MM-DD', true)
            return start.isValid() ? start.startOf('day').toDate() : null
        }
    }
}

/**
 * Quando o item entrou no estado atual. _stateChangedAt é o campo certo, mas é
 * recente: linhas anteriores à migração não o têm, e para elas _updatedAt/_createdAt
 * (preenchidos pelo backfill a partir do ObjectId) são a melhor aproximação.
 * Chegam como ISO datetime, não como 'YYYY-MM-DD' — nada de somar 'T00:00:00' aqui.
 */
export function finishedAt(instance: any): Date | null {
    const raw = instance._stateChangedAt || instance._updatedAt || instance._createdAt
    if (!raw) return null
    const date = new Date(raw)
    return isNaN(date.getTime()) ? null : date
}

/** Sem data confiável nunca esconde: melhor um item a mais do que um sumiço silencioso. */
export function isWithin(instance: any, period: FinishedPeriod, now: Date = new Date()): boolean {
    const start = periodStart(period, now)
    if (!start) return true
    const at = finishedAt(instance)
    if (!at) return true
    return at.getTime() >= start.getTime()
}

/**
 * Guard para o que vem do localStorage: qualquer lixo vira null e o chamador cai no
 * padrão. Conteúdo velho ou adulterado não pode derrubar a página.
 */
export function asFinishedPeriod(obj: any): FinishedPeriod | null {
    if (!obj || typeof obj !== 'object') return null
    if (obj.kind === 'all') return {kind: 'all'}
    if (obj.kind === 'last') {
        const amount = Number(obj.amount)
        if (!Number.isInteger(amount) || amount < 1) return null
        if (!UNITS.includes(obj.unit)) return null
        return {kind: 'last', amount, unit: obj.unit}
    }
    if (obj.kind === 'current') {
        if (!CURRENT_UNITS.includes(obj.unit)) return null
        return {kind: 'current', unit: obj.unit}
    }
    if (obj.kind === 'since') {
        if (typeof obj.date !== 'string' || !moment(obj.date, 'YYYY-MM-DD', true).isValid()) return null
        return {kind: 'since', date: obj.date}
    }
    return null
}
